import { createContext, useState } from 'react';
import { CompanyClient, ContactPeople } from '../../../../../api/service/superAdmin/company-clients/types';
import { INITIAL_CONTACT_PEOPLE, INITIAL_COMPANY_DETAILS, INITIAL_ADDRESS_DETAILS } from '../contants';
import {
  AccountingFirm,
  AddressDetails,
  AdminDetails,
  AuthorizedSignatory,
  AuthorizedSignatoryGroup,
  CollectiveAgreementType,
  CompanyClientFormGroup,
  CompanyDetails,
  Consultants,
  ICompanyClientContext,
} from '../GeneralForms/types';
import { useFormik } from 'formik';
import { formSchema } from './schema';
import { Deployment } from '../../../../superAdmin/CompanyClients/types';

export const EditGeneralSettingsContext = createContext<ICompanyClientContext>({
  companyRes: null,
  setCompanyRes: () => {},

  companyDetails: INITIAL_COMPANY_DETAILS,
  setCompanyDetails: () => {},

  adminDetails: [],
  setAdminDetails: () => {},

  signatoryDetails: [],
  setSignatoryDetails: () => {},

  addressDetails: INITIAL_ADDRESS_DETAILS,
  setAddressDetails: () => {},

  salaryContactPeople: INITIAL_CONTACT_PEOPLE,
  setSalaryContactPeople: () => {},

  accountingContactPeople: INITIAL_CONTACT_PEOPLE,
  setAccountingContactPeople: () => {},

  contractEffectiveFrom: '',
  setContractEffectiveFrom: () => {},

  collectiveAgreements: [],
  setCollectiveAgreements: () => {},

  invoiceCostCenter: '',
  setInvoiceCostCenter: () => {},

  contractEmail: '',
  setContractEmail: () => {},

  consultants: [],
  setConsultants: () => {},

  accountingFirms: [],
  setAccountingFirms: () => {},

  invitationEmail: '',
  setInvitationEmail: () => {},

  contractNumber: '',
  setContractNumber: () => {},

  combinations: [],
  setCombinations: () => {},

  deletedAdmins: [],
  setDeletedAdmins: () => {},

  deletedConsultants: [],
  setDeletedConsultants: () => {},

  signatoryAdmins: [],
  setSignatoryAdmins: () => {},

  allowCorrectoRemoteAccess: false,
  setAllowCorrectoRemoteAccess: () => {},

  allowAdvisorRemoteAccess: false,
  setAllowAdvisorRemoteAccess: () => {},

  isEditMode: true,
  formGroup: {} as CompanyClientFormGroup,

  deployment: { systemDeploymentMonth: null, employeeInvitationSendOuts: null },
  setDeployment: () => {},
  updateDeployment: () => {},
  replaceDeployment: () => {},

  replaceAdminDetails: () => {},
  replaceAddress: () => {},
  replaceAuthorizedSignatory: () => {},
  replaceCompanyDetails: () => {},
  replaceConsultants: () => {},
  replaceConsultant: () => {},
  replaceAccountingContactPeople: () => {},
  replaceSalaryContactPeople: () => {},
  updateAdminDetails: () => {},
  updateAddress: () => {},
  updateAuthorizedSignatory: () => {},
  updateCompanyDetails: () => {},
  updateConsultants: () => {},
  updateConsultant: () => {},
  updateAccountingContactPeople: () => {},
  updateSalaryContactPeople: () => {},
  updateContractEffectiveInput: () => {},
  updateInvoicingCostCenter: () => {},
  updateInvoiceEmailAddress: () => {},
  replaceCollectiveAgreements: () => {},
  updateInvitationEmail: () => {},
  updateContractNumber: () => {},
  replaceCombinations: () => {},
  updateCombinations: () => {},
  appendDeletedAdmin: () => {},
  appendDeletedConsultant: () => {},
  replaceSignatoryAdmins: () => {},
  replaceAccess: () => {},
  replaceDeletedAdmins: () => {},
});

type Props = {
  children: JSX.Element;
};

export const EditGeneralSettingsProvider = ({ children }: Props) => {
  const [companyRes, setCompanyRes] = useState<CompanyClient | null>(null);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>(INITIAL_COMPANY_DETAILS);
  const [addressDetails, setAddressDetails] = useState<AddressDetails>(INITIAL_ADDRESS_DETAILS);
  const [adminDetails, setAdminDetails] = useState<Array<AdminDetails>>([]);
  const [signatoryDetails, setSignatoryDetails] = useState<Array<AuthorizedSignatory>>([]);
  const [salaryContactPeople, setSalaryContactPeople] = useState<ContactPeople>(INITIAL_CONTACT_PEOPLE);
  const [accountingContactPeople, setAccountingContactPeople] = useState<ContactPeople>(INITIAL_CONTACT_PEOPLE);
  const [contractEffectiveFrom, setContractEffectiveFrom] = useState<string>('');
  const [invoiceCostCenter, setInvoiceCostCenter] = useState<string>('');
  const [contractEmail, setContractEmail] = useState<string>('');
  const [collectiveAgreements, setCollectiveAgreements] = useState<Array<CollectiveAgreementType>>([]);
  const [accountingFirms, setAccountingFirms] = useState<Array<AccountingFirm>>([]);
  const [consultants, setConsultants] = useState<Array<Consultants>>([]);
  const [invitationEmail, setInvitationEmail] = useState<string>('');
  const [contractNumber, setContractNumber] = useState<string>('');
  const [combinations, setCombinations] = useState<Array<AuthorizedSignatoryGroup>>([]);
  const [deletedAdmins, setDeletedAdmins] = useState<Array<string>>([]);
  const [deletedConsultants, setDeletedConsultants] = useState<Array<string>>([]);
  const [signatoryAdmins, setSignatoryAdmins] = useState<Array<AuthorizedSignatory>>([]);
  const [allowCorrectoRemoteAccess, setAllowCorrectoRemoteAccess] = useState(false);
  const [allowAdvisorRemoteAccess, setAllowAdvisorRemoteAccess] = useState(false);
  const [deployment, setDeployment] = useState<{
    systemDeploymentMonth: string | null;
    employeeInvitationSendOuts: string | null;
  }>({ systemDeploymentMonth: null, employeeInvitationSendOuts: null });

  const CLIENT_INITIAL_VALUES = {
    companyDetails: { ...INITIAL_COMPANY_DETAILS },
    address: INITIAL_ADDRESS_DETAILS,
    contractEffectiveFrom: '',
    contractEmail: '',
    invoiceCostCenter: '',
    authorizedSignatory: [],
    admins: [],
    consultants: [],
    invitationEmail: '',
    salaryContactPeople: { ...INITIAL_CONTACT_PEOPLE },
    accountingContactPeople: { ...INITIAL_CONTACT_PEOPLE },
    contractNumber: '',
    combinations: [],
    allowCorrectoAccess: false,
    allowAdvisorAccess: false,
    deletedConsultants: [],
  };

  const formGroup = useFormik({
    initialValues: CLIENT_INITIAL_VALUES,
    validateOnMount: true,
    validationSchema: formSchema,
    onSubmit: () => {},
  });

  const replaceAdminDetails = (value) => {
    setAdminDetails(value);
    formGroup.setFieldValue('admins', value);
  };

  const replaceAddress = (value) => {
    setAddressDetails(value);
    formGroup.setFieldValue('address', value);
  };

  const replaceAuthorizedSignatory = (value) => {
    const authorizedSignatoryValue = value || [];

    setSignatoryDetails(authorizedSignatoryValue);
    formGroup.setFieldValue('authorizedSignatory', authorizedSignatoryValue);
    formGroup.setFieldTouched('address');
  };

  const replaceCompanyDetails = (value) => {
    setCompanyDetails(value);
    formGroup.setFieldValue('companyDetails', value);
    formGroup.setFieldTouched('companyDetails');
  };

  const replaceConsultants = (value) => {
    setConsultants(value);
    formGroup.setFieldValue('consultants', value);
  };
  const replaceConsultant = (value) => {
    formGroup.setFieldValue('consultant', value);
  };
  const replaceAccountingContactPeople = (value) => {
    setAccountingContactPeople(value);
    formGroup.setFieldValue('accountingContactPeople', value);
  };

  const replaceSalaryContactPeople = (value) => {
    setSalaryContactPeople(value);
    formGroup.setFieldValue('salaryContactPeople', value);
  };

  const updateAddress = (inputName, inputValue) => {
    setAddressDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`address.${inputName}`, inputValue);
  };

  const updateAdminDetails = (inputName, inputValue) => {
    setAdminDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`admins.${inputName}`, inputValue);
  };

  const updateAuthorizedSignatory = (inputName, inputValue) => {
    setSignatoryDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`authorizedSignatory.${inputName}`, inputValue);
  };

  const updateCompanyDetails = (inputName, inputValue) => {
    setCompanyDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`companyDetails.${inputName}`, inputValue);
  };

  const updateConsultants = (inputName, inputValue) => {
    setConsultants((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`consultants.${inputName}`, inputValue);
  };

  const updateConsultant = (inputName, inputValue) => {
    formGroup.setFieldValue(`consultant.${inputName}`, inputValue);
  };

  const updateAccountingContactPeople = (inputName, inputValue) => {
    setAccountingContactPeople((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`accountingContactPeople.${inputName}`, inputValue);
  };

  const updateSalaryContactPeople = (inputName, inputValue) => {
    setSalaryContactPeople((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`salaryContactPeople.${inputName}`, inputValue);
  };

  const updateContractEffectiveInput = (inputValue) => {
    setContractEffectiveFrom(inputValue);
    formGroup.setFieldValue(`contractEffectiveFrom`, inputValue);
  };

  const updateInvoicingCostCenter = (inputValue) => {
    setInvoiceCostCenter(inputValue);
    formGroup.setFieldValue(`invoiceCostCenter`, inputValue);
  };

  const updateInvoiceEmailAddress = (inputValue) => {
    setContractEmail(inputValue);
    formGroup.setFieldValue(`contractEmail`, inputValue);
  };

  const replaceCollectiveAgreements = (inputValue) => {
    setCollectiveAgreements(inputValue);
    formGroup.setFieldValue('collectiveAgreements', inputValue);
  };

  const updateInvitationEmail = (inputValue) => {
    setInvitationEmail(inputValue);
    formGroup.setFieldValue(`invitationEmail`, inputValue);
  };

  const updateContractNumber = (inputValue) => {
    setContractNumber(inputValue);
    formGroup.setFieldValue(`contractNumber`, inputValue);
  };

  const replaceCombinations = (value) => {
    setCombinations(value);
    formGroup.setFieldValue('combinations', value);
  };

  const updateCombinations = (inputName, inputValue) => {
    setCombinations((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`combinations.${inputName}`, inputValue);
  };

  const appendDeletedAdmin = (value) => {
    const _deletedAdmins = [...deletedAdmins];
    _deletedAdmins.push(value);

    setDeletedAdmins(_deletedAdmins);
  };

  const appendDeletedConsultant = (value) => {
    // if (!isEditMode) {
    //   return;
    // }

    const _deletedConsultants = [...deletedConsultants];
    _deletedConsultants.push(value);

    setDeletedConsultants(_deletedConsultants);
  };

  const replaceSignatoryAdmins = (value) => {
    setSignatoryAdmins(value);
    formGroup.setFieldValue('signatoryAdmins', value);
  };

  const replaceAccess = (correctoAccess, advisorAccess) => {
    setAllowAdvisorRemoteAccess(advisorAccess);
    setAllowCorrectoRemoteAccess(correctoAccess);
    formGroup.setFieldValue('allowCorrectoAccess', correctoAccess);
    formGroup.setFieldValue('allowAdvisorAccess', advisorAccess);
  };

  const replaceDeletedAdmins = (value) => {
    setDeletedAdmins(value);
  };

  const updateDeployment = (inputName: keyof typeof deployment, inputValue: string | null) => {
    setDeployment((prev) => ({
      ...prev,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`deployment.${inputName}`, inputValue);
  };

  const replaceDeployment = (deployment: Deployment) => {
    setDeployment(deployment);
    formGroup.setFieldValue('deployment', deployment);
  };

  const context: ICompanyClientContext = {
    companyRes,
    setCompanyRes,
    companyDetails,
    setCompanyDetails,
    adminDetails,
    setAdminDetails,
    signatoryDetails,
    setSignatoryDetails,
    addressDetails,
    setAddressDetails,
    salaryContactPeople,
    setSalaryContactPeople,
    accountingContactPeople,
    setAccountingContactPeople,
    contractEffectiveFrom,
    setContractEffectiveFrom,
    combinations,
    setCombinations,
    isEditMode: true,
    collectiveAgreements,
    setCollectiveAgreements,
    invoiceCostCenter,
    setInvoiceCostCenter,
    contractEmail,
    setContractEmail,
    consultants,
    setConsultants,
    accountingFirms,
    setAccountingFirms,
    invitationEmail,
    setInvitationEmail,
    contractNumber,
    setContractNumber,
    deletedAdmins,
    setDeletedAdmins,
    deletedConsultants,
    setDeletedConsultants,
    signatoryAdmins,
    setSignatoryAdmins,
    allowAdvisorRemoteAccess,
    setAllowAdvisorRemoteAccess,
    allowCorrectoRemoteAccess,
    setAllowCorrectoRemoteAccess,
    formGroup: formGroup as any,
    deployment,
    setDeployment,
    updateDeployment,
    replaceDeployment,
    replaceAdminDetails,
    replaceAddress,
    replaceAuthorizedSignatory,
    replaceCompanyDetails,
    replaceConsultants,
    replaceConsultant,
    replaceAccountingContactPeople,
    replaceSalaryContactPeople,
    replaceCombinations,
    updateAdminDetails,
    updateAddress,
    updateAuthorizedSignatory,
    updateCompanyDetails,
    updateConsultants,
    updateConsultant,
    updateAccountingContactPeople,
    updateSalaryContactPeople,
    updateContractEffectiveInput,
    updateInvoiceEmailAddress,
    updateInvoicingCostCenter,
    replaceCollectiveAgreements,
    updateInvitationEmail,
    updateContractNumber,
    updateCombinations,
    appendDeletedAdmin,
    appendDeletedConsultant,
    replaceSignatoryAdmins,
    replaceAccess,
    replaceDeletedAdmins,
  };

  return <EditGeneralSettingsContext.Provider value={context}>{children}</EditGeneralSettingsContext.Provider>;
};
